import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, IconButton, MenuItem, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DoNotDisturbOnOutlinedIcon from '@mui/icons-material/DoNotDisturbOnOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import {
  changeConsultResult, exportConsultManageList, getConsultManageList, getConsultManagers,
} from '../../api/consultManage';
import { CONSULT_RESULT_COLORS, CONSULT_RESULT_LABELS, getConsultMethodLabel, getConsultPurposeLabel } from '../../constants/consultation';
import { getVisitPathLabel } from '../../constants/visitPath';
import { CommonExcelButton } from '../../form-components/CommonExcelButton';
import { errorMessages, exportStyledExcel } from '../../utils/methods';
import CustomCheckbox from '../CustomCheckbox';
import { FloatingActionBar, FloatingBarInfo, FloatingCloseButton } from '../member/memberList/MemberListStyled';
import { HeaderSummaryChips, SummaryChipDivider, SummaryChipItem } from '../lesson/privateLesson/PrivateLessonStyled';
import { bodyCellSx, dateFieldSx, headCellSx, searchButtonSx } from './classManageShared';
import ScheduleDateLink from './ScheduleDateLink';

// 수업·상담 관리 > 상담 목록. 수업 목록과 같은 UI 에 상담 데이터. 결과는 [변경]·일괄로 바꾸고,
// 신규등록·재등록은 판매등록으로만 바뀐다(결제 없는 '등록' 방지 — 사용자 결정 10/15). 미확인은 새벽 배치가 만든다.
const PAGE_SIZE = 50;
const DATE_FORMAT = 'YYYY-MM-DD';
const MAX_PERIOD_MONTHS = 6;

// 직원이 직접 고를 수 있는 결과(백엔드 ConsultManageService.MANUAL_RESULTS 와 같음)
const MANUAL_RESULTS = ['SCHEDULED', 'NO_SHOW', 'VISITED', 'CONSIDERING', 'NOT_REGISTERED'];
// 한 번에 바꿀 수 있는 상담 수(백엔드 ConsultManageService.MAX_CHANGE_COUNT 와 같음)
const MAX_CHANGE_COUNT = 500;
// 건수 칩 순서. RESCHEDULED 는 건수가 있을 때만 보인다
const RESULT_CHIPS = ['SCHEDULED', 'UNCONFIRMED', 'NO_SHOW', 'VISITED', 'CONSIDERING', 'NOT_REGISTERED', 'NEW_REGISTERED', 'RE_REGISTERED'];
const CONSULT_TYPE_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'MEMBER', label: '회원' },
  { value: 'TEMPORARY', label: '잠재회원' },
  { value: 'NON_MEMBER', label: '비회원' },
];
const CONSULT_TYPE_LABEL = { MEMBER: '회원', TEMPORARY: '잠재회원', NON_MEMBER: '비회원' };

const defaultFilter = () => ({
  startDate: dayjs().subtract(30, 'day').format(DATE_FORMAT),
  endDate: dayjs().add(7, 'day').format(DATE_FORMAT),
  result: '',
  managerId: '',
  consultType: '',
  member: '',
});

const timeText = row => (row.startTime ? `${row.startTime}~${row.endTime || ''}` : '종일');

function ResultBadge({ result }) {
  const color = CONSULT_RESULT_COLORS[result] || '#666';
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block', px: '8px', py: '2px', borderRadius: '10px', fontSize: '12px', fontWeight: 600,
        color, background: `${color}14`, whiteSpace: 'nowrap',
      }}>
      {CONSULT_RESULT_LABELS[result] || result}
    </Box>
  );
}

function InfoRow({ label, value, multiline }) {
  return (
    <Box sx={{ display: 'flex', gap: '12px', py: '5px' }}>
      <Typography sx={{ width: 64, flexShrink: 0, fontSize: '13px', color: '#8C8C8C' }}>{label}</Typography>
      <Typography
        sx={{
          fontSize: '13px', color: '#262626', fontWeight: 500, wordBreak: 'keep-all', whiteSpace: 'pre-wrap',
          ...(multiline && { maxHeight: 88, overflowY: 'auto', fontWeight: 400 }),
        }}>
        {value || '-'}
      </Typography>
    </Box>
  );
}

// 결과 선택 카드 아이콘
const RESULT_ICONS = {
  SCHEDULED: EventOutlinedIcon,
  NO_SHOW: PersonOffOutlinedIcon,
  VISITED: StorefrontOutlinedIcon,
  CONSIDERING: HelpOutlineIcon,
  NOT_REGISTERED: DoNotDisturbOnOutlinedIcon,
};

// 상담 결과 변경 모달(출결 변경 모달과 같은 모양). rows = 대상(한 건이면 상세 정보를 보여 준다)
function ChangeResultModal({ rows, onClose, onConfirm, isPending }) {
  const [result, setResult] = useState(null);
  useEffect(() => setResult(null), [rows]);
  if (!rows) return null;
  const single = rows.length === 1 ? rows[0] : null;
  const currentColor = single && (CONSULT_RESULT_COLORS[single.consultResult] || '#666');
  return (
    <Dialog open={!!rows} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, pt: 2.5, pb: 1 }}>
        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#262626' }}>
          {single ? '상담 결과 변경' : `상담 ${rows.length}건 결과 변경`}
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="닫기" sx={{ color: '#8C8C8C' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <DialogContent sx={{ px: 3, pt: 1 }}>
        {/* 대상·상담 요약 */}
        {single && (
          <Box sx={{ border: '1px solid #EDEDED', borderRadius: '12px', background: '#FAFAFA', p: '14px 16px', mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', mb: 1 }}>
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#262626' }}>{single.targetName || '-'}</Typography>
                  <Typography component="span" sx={{ fontSize: '11px', color: '#595959', background: '#EFEFEF', borderRadius: '4px', px: '6px', py: '1px' }}>
                    {CONSULT_TYPE_LABEL[single.consultType] || '-'}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '12px', color: '#8C8C8C' }}>{single.phoneNumber || ''}</Typography>
              </Box>
              <Box
                component="span"
                sx={{
                  flexShrink: 0, px: '10px', py: '3px', borderRadius: '12px', fontSize: '12px', fontWeight: 600,
                  color: currentColor, background: `${currentColor}14`,
                }}>
                현재 {CONSULT_RESULT_LABELS[single.consultResult] || single.consultResult}
              </Box>
            </Box>
            <Box sx={{ borderTop: '1px dashed #E0E0E0', pt: 1 }}>
              <InfoRow label="상담" value={`${single.consultDate} ${timeText(single)}`} />
              <InfoRow label="담당자" value={single.managerName} />
              <InfoRow
                label="수단·목적"
                value={[
                  single.consultMethod && getConsultMethodLabel(single.consultMethod),
                  single.consultPurpose && getConsultPurposeLabel(single.consultPurpose),
                ].filter(Boolean).join(' · ')}
              />
              <InfoRow label="메모" value={single.content} multiline />
            </Box>
          </Box>
        )}

        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#262626', mb: 1 }}>변경할 결과</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
          {MANUAL_RESULTS.map(value => {
            const color = CONSULT_RESULT_COLORS[value];
            const Icon = RESULT_ICONS[value];
            const current = single?.consultResult === value;
            const selected = result === value;
            return (
              <Button
                key={value}
                disabled={current || isPending}
                onClick={() => setResult(value)}
                sx={{
                  display: 'flex', flexDirection: 'column', gap: '4px', height: '76px', borderRadius: '12px',
                  fontSize: '13px', fontWeight: 600, textTransform: 'none',
                  color: selected ? color : '#595959',
                  border: `1.5px solid ${selected ? color : '#E5E5E5'}`,
                  background: selected ? `${color}14` : '#fff',
                  transition: 'all 0.15s',
                  '&:hover': { borderColor: color, color, background: `${color}14` },
                  '&.Mui-disabled': { color: '#BFBFBF', background: '#FAFAFA', borderColor: '#EFEFEF' },
                }}>
                <Icon sx={{ fontSize: '22px' }} />
                <span>{CONSULT_RESULT_LABELS[value]}{current && ' (현재)'}</span>
              </Button>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: '8px' }}>
        <Button
          onClick={onClose}
          sx={{
            flex: 1, height: '42px', borderRadius: '8px', border: '1px solid #D9D9D9', color: '#595959', fontWeight: 600,
            '&:hover': { background: '#F5F5F5', borderColor: '#D9D9D9' },
          }}>
          닫기
        </Button>
        <Button
          disabled={!result || isPending}
          onClick={() => onConfirm(result)}
          sx={{
            flex: 1, height: '42px', borderRadius: '8px', background: '#262626', color: '#fff', fontWeight: 600,
            '&:hover': { background: '#000' },
            '&.Mui-disabled': { background: '#E5E5E5', color: '#fff' },
          }}>
          {isPending ? '변경 중...' : '변경'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ConsultList({ branchId }) {
  const user = useSelector(state => state.user.user);
  const toast = useToast();
  const queryClient = useQueryClient();

  const [form, setForm] = useState(defaultFilter);
  const [query, setQuery] = useState(defaultFilter);
  const [searchText, setSearchText] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [changeRows, setChangeRows] = useState(null);
  const [isExcelLoading, setIsExcelLoading] = useState(false);
  const rowCache = useRef(new Map());
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);

  const { data: managers = [] } = useQuery({
    queryKey: ['consultManagers', branchId],
    queryFn: () => getConsultManagers(branchId, user.token),
    enabled: !!branchId && !!user?.token,
  });

  const { data, isLoading, isError, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['consultManageList', branchId, query],
      initialPageParam: 0,
      queryFn: async ({ pageParam }) => {
        const res = await getConsultManageList({ branchId, ...query, page: pageParam, size: PAGE_SIZE }, user.token);
        (res?.content || []).forEach(row => rowCache.current.set(row.id, row));
        return res;
      },
      getNextPageParam: lastPage =>
        lastPage && lastPage.page + 1 < lastPage.totalPages ? lastPage.page + 1 : undefined,
      enabled: !!branchId && !!user?.token,
    });

  const rows = useMemo(() => (data?.pages || []).flatMap(p => p?.content || []), [data]);
  const totalElements = data?.pages?.[0]?.totalElements ?? 0;
  const resultCounts = data?.pages?.[0]?.resultCounts || {};
  const allChecked = rows.length > 0 && rows.every(row => selectedIds.has(row.id));

  // 지점을 바꾸면 이전 지점의 선택·줄 캐시를 버린다
  useEffect(() => {
    setSelectedIds(new Set());
    rowCache.current = new Map();
  }, [branchId]);

  useEffect(() => {
    const target = sentinelRef.current;
    if (!target || !hasNextPage) return undefined;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && !isFetchingNextPage) fetchNextPage();
      },
      { root: scrollRef.current, rootMargin: '200px' },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, rows.length]);

  const changeMutation = useMutation({
    mutationFn: ({ ids, result }) => changeConsultResult(branchId, { result, consultationIds: ids }, user.token),
    onSuccess: res => {
      const failed = res?.failures?.length || 0;
      if (failed) toast.warning(`${res.successCount}건 변경, ${failed}건 실패: ${res.failures[0].message}`);
      else toast.success(`${res?.successCount ?? 0}건 변경되었습니다`);
      setChangeRows(null);
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['consultManageList'] });
      queryClient.invalidateQueries({ queryKey: ['consultStats'] });
    },
    onError: err => toast.error(errorMessages(err)),
  });

  const runSearch = next => {
    if (next.startDate > next.endDate) {
      toast.error('시작일이 종료일보다 늦을 수 없습니다.');
      return;
    }
    if (dayjs(next.endDate).isAfter(dayjs(next.startDate).add(MAX_PERIOD_MONTHS, 'month'))) {
      toast.error(`조회 기간은 최대 ${MAX_PERIOD_MONTHS}개월입니다.`);
      return;
    }
    if (JSON.stringify(next) === JSON.stringify(query)) refetch();
    setForm(next);
    setQuery(next);
    setSelectedIds(new Set());
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  const addSearchChip = () => {
    const text = searchText.trim();
    if (!text) return;
    setSearchText('');
    runSearch({ ...form, member: text });
  };

  const setField = key => e => setForm(prev => ({ ...prev, [key]: e.target.value }));

  // CustomCheckbox 는 클릭 1회에 핸들러가 2번 불리므로 체크 값으로 넣고 뺀다
  const setRowChecked = (id, checked) =>
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  const setAllChecked = checked =>
    setSelectedIds(prev => {
      const next = new Set(prev);
      rows.forEach(row => (checked ? next.add(row.id) : next.delete(row.id)));
      return next;
    });

  const handleExcelDownload = async () => {
    setIsExcelLoading(true);
    try {
      const all = (await exportConsultManageList({ branchId, ...query }, user.token)) || [];
      if (all.length === 0) {
        toast.info('다운로드할 내역이 없습니다');
        return;
      }
      await exportStyledExcel(
        all.map(row => ({
          상담일: row.consultDate ?? '',
          시간: timeText(row),
          대상구분: CONSULT_TYPE_LABEL[row.consultType] ?? '',
          이름: row.targetName ?? '',
          연락처: row.phoneNumber ?? '',
          담당자: row.managerName ?? '',
          상담수단: row.consultMethod ? getConsultMethodLabel(row.consultMethod) : '',
          상담목적: row.consultPurpose ? getConsultPurposeLabel(row.consultPurpose) : '',
          방문경로: row.visitPath ? getVisitPathLabel(row.visitPath) : '',
          결과: CONSULT_RESULT_LABELS[row.consultResult] ?? row.consultResult ?? '',
          메모: row.content ?? '',
        })),
        { filename: `상담목록_${query.startDate}_${query.endDate}`, sheetName: '상담 목록' },
      );
    } catch (err) {
      toast.error(errorMessages(err));
    } finally {
      setIsExcelLoading(false);
    }
  };

  const chipKeys = RESULT_CHIPS.concat(resultCounts.RESCHEDULED ? ['RESCHEDULED'] : []);
  const chipTotal = Object.values(resultCounts).reduce((sum, n) => sum + n, 0);

  return (
    <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px' }}>
        <TextField
          type="date" size="small" label="상담일 시작" InputLabelProps={{ shrink: true }} sx={dateFieldSx}
          value={form.startDate} onChange={setField('startDate')}
        />
        <TextField
          type="date" size="small" label="상담일 종료" InputLabelProps={{ shrink: true }} sx={dateFieldSx}
          value={form.endDate} onChange={setField('endDate')}
        />
        <TextField select size="small" label="결과" sx={{ minWidth: 120 }} value={form.result} onChange={setField('result')}>
          <MenuItem value="">전체</MenuItem>
          {Object.entries(CONSULT_RESULT_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="담당자" sx={{ minWidth: 130 }} value={form.managerId} onChange={setField('managerId')}>
          <MenuItem value="">전체</MenuItem>
          {managers.map(m => <MenuItem key={m.managerId} value={m.managerId}>{m.managerName}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="대상" sx={{ minWidth: 110 }} value={form.consultType} onChange={setField('consultType')}>
          {CONSULT_TYPE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        <TextField
          size="small" label="회원 검색" placeholder="이름 또는 연락처" sx={{ minWidth: 180 }}
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.nativeEvent.isComposing) addSearchChip();
          }}
        />
        <Button variant="outlined" onClick={() => (searchText.trim() ? addSearchChip() : runSearch(form))} sx={searchButtonSx}>
          조회
        </Button>
        {query.member && (
          <Chip
            size="small" label={`회원: ${query.member}`} onDelete={() => runSearch({ ...form, member: '' })}
            sx={{ height: 30, fontSize: '13px', background: '#F2F5F6', border: '1px solid #E4E9EA' }}
          />
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        {/* 결과별 건수(결과 필터만 뺀 같은 조건). 누르면 그 결과만, 다시 누르면 전체 */}
        <HeaderSummaryChips>
          <SummaryChipItem type="button" countColor="#5A6468" isActive={false} title="전체 보기" onClick={() => runSearch({ ...query, result: '' })}>
            전체
            <b>{chipTotal.toLocaleString()}건</b>
          </SummaryChipItem>
          {chipKeys.map(key => {
            const active = query.result === key;
            return (
              <React.Fragment key={key}>
                <SummaryChipDivider>|</SummaryChipDivider>
                <SummaryChipItem
                  type="button" countColor={CONSULT_RESULT_COLORS[key]} isActive={active} aria-pressed={active}
                  onClick={() => runSearch({ ...query, result: active ? '' : key })}>
                  {CONSULT_RESULT_LABELS[key]}
                  <b>{(resultCounts[key] ?? 0).toLocaleString()}건</b>
                </SummaryChipItem>
              </React.Fragment>
            );
          })}
        </HeaderSummaryChips>
        <CommonExcelButton onClick={handleExcelDownload} disabled={isExcelLoading || !totalElements}>
          {isExcelLoading ? '다운로드 중...' : '엑셀 다운로드'}
        </CommonExcelButton>
      </Box>

      <TableContainer
        ref={scrollRef}
        sx={{
          border: '1px solid #E8E8E8', borderRadius: '8px', overflowY: 'auto',
          maxHeight: selectedIds.size > 0 ? 'calc(100vh - 360px)' : 'calc(100vh - 280px)',
        }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ ...headCellSx, width: 48, px: 0 }}>
                <CustomCheckbox
                  name="consultAllSelect" checked={allChecked} disabled={rows.length === 0}
                  onChange={e => setAllChecked(e.target.checked)}
                />
              </TableCell>
              {['상담일', '시간', '대상', '이름', '연락처', '담당자', '수단', '목적', '방문경로', '결과', '처리'].map(h => (
                <TableCell key={h} align="center" sx={headCellSx}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && <TableRow><TableCell colSpan={12} align="center">불러오는 중...</TableCell></TableRow>}
            {isError && (
              <TableRow><TableCell colSpan={12} align="center" sx={{ color: '#d32f2f' }}>{errorMessages(error)}</TableCell></TableRow>
            )}
            {!isLoading && !isError && rows.length === 0 && (
              <TableRow><TableCell colSpan={12} align="center" sx={{ py: 6, color: '#888' }}>조회된 상담이 없습니다.</TableCell></TableRow>
            )}
            {rows.map(row => (
              <TableRow key={row.id} hover selected={selectedIds.has(row.id)}>
                <TableCell align="center" sx={{ width: 48, px: 0 }}>
                  <CustomCheckbox
                    name={`consult-${row.id}`} checked={selectedIds.has(row.id)}
                    onChange={e => setRowChecked(row.id, e.target.checked)}
                  />
                </TableCell>
                <TableCell align="center" sx={bodyCellSx}>
                  <ScheduleDateLink date={row.consultDate} consultationId={row.id} />
                </TableCell>
                <TableCell align="center" sx={bodyCellSx}>{timeText(row)}</TableCell>
                <TableCell align="center" sx={bodyCellSx}>{CONSULT_TYPE_LABEL[row.consultType] || '-'}</TableCell>
                <TableCell align="center" sx={bodyCellSx}>{row.targetName || '-'}</TableCell>
                <TableCell align="center" sx={bodyCellSx}>{row.phoneNumber || '-'}</TableCell>
                <TableCell align="center" sx={bodyCellSx}>{row.managerName || '-'}</TableCell>
                <TableCell align="center" sx={bodyCellSx}>{row.consultMethod ? getConsultMethodLabel(row.consultMethod) : '-'}</TableCell>
                <TableCell align="center" sx={bodyCellSx}>{row.consultPurpose ? getConsultPurposeLabel(row.consultPurpose) : '-'}</TableCell>
                <TableCell align="center" sx={bodyCellSx}>{row.visitPath ? getVisitPathLabel(row.visitPath) : '-'}</TableCell>
                <TableCell align="center" sx={bodyCellSx}><ResultBadge result={row.consultResult} /></TableCell>
                <TableCell align="center" sx={bodyCellSx}>
                  <Button
                    size="small" variant="outlined" onClick={() => setChangeRows([row])}
                    sx={{ minWidth: 0, px: 1.5, fontSize: '12px', borderColor: '#999', color: '#555' }}>
                    변경
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Box ref={sentinelRef} sx={{ height: '1px' }} />
        {isFetchingNextPage && (
          <Typography sx={{ textAlign: 'center', py: 1.5, fontSize: '13px', color: '#888' }}>불러오는 중...</Typography>
        )}
      </TableContainer>

      {selectedIds.size > 0 && (
        <FloatingActionBar sx={{ gap: '12px', padding: '10px 12px 10px 20px' }}>
          <FloatingBarInfo>
            <Box component="span" sx={{ color: '#0F7F85' }}>{selectedIds.size}</Box>건 선택됨
          </FloatingBarInfo>
          <Box sx={{ width: '1px', height: '20px', background: '#E5E5E5' }} />
          <Button
            variant="outlined"
            onClick={() => {
              if (selectedIds.size > MAX_CHANGE_COUNT) {
                toast.error(`한 번에 ${MAX_CHANGE_COUNT}건까지 바꿀 수 있습니다.`);
                return;
              }
              setChangeRows([...selectedIds].map(id => rowCache.current.get(id)).filter(Boolean));
            }}
            sx={{
              minWidth: '96px', height: '34px', borderRadius: '6px', fontSize: '13px', fontWeight: 600,
              borderColor: '#1976D2', color: '#1976D2', '&:hover': { borderColor: '#1976D2', background: 'rgba(25, 118, 210, 0.06)' },
            }}>
            결과 변경
          </Button>
          <FloatingCloseButton onClick={() => setSelectedIds(new Set())} aria-label="선택 해제">×</FloatingCloseButton>
        </FloatingActionBar>
      )}

      <ChangeResultModal
        rows={changeRows}
        onClose={() => setChangeRows(null)}
        isPending={changeMutation.isPending}
        onConfirm={result => changeMutation.mutate({ ids: changeRows.map(r => r.id), result })}
      />

    </Box>
  );
}

export default ConsultList;
